import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router';
import type { PermissionMode, PermissionRequirement } from '@erp/connect/shell';

/**
 * The slice of the erp permission store the gate needs — kept structural so
 * the decision is a pure function and unit-testable without pinia.
 */
export type PermissionGateStore = {
  /** true only when a permission snapshot was successfully loaded */
  loaded: boolean;
  /** null when the server said the account has no employee; undefined = unknown */
  employeeId?: number | null;
  can(requirement: PermissionRequirement, mode?: PermissionMode): boolean;
};

/**
 * Decides where a permission-gated erp navigation goes (BLP/nx#1139).
 *
 * Two failure states that used to collapse onto one page are kept apart:
 *
 * - `loaded === false` on a gated route → the snapshot could NOT be fetched
 *   (erp module down, gateway 503 no_clients_connected, timeout). That is an
 *   availability problem, not an authorization verdict — route to the
 *   "service unavailable" page with a retry, NOT to permission-denied.
 * - loaded but a required key missing → the real permission-denied.
 *
 * Ungated routes pass in both states (the shell's resilience decision: a
 * degraded permission path must not lock the user out of intentionally
 * ungated surfaces; the backend stays the real authorization wall).
 */
export function resolveErpPermissionGate(
  to: RouteLocationNormalized,
  permissions: PermissionGateStore
): true | RouteLocationRaw {
  // An account without an employee cannot save anything in ERP (the backend
  // write gate refuses it), so it does not get in at all — every ERP route,
  // gated or not. Only a definite "none" from a loaded snapshot blocks; an
  // unknown state keeps the resilience decision above.
  if (permissions.loaded && permissions.employeeId === null) {
    return { path: '/erp/no-employee', replace: true };
  }
  for (const match of to.matched) {
    const requirement = match.meta.requiredPermissions;
    if (!requirement) continue;
    if (!permissions.loaded) {
      return {
        path: '/erp/service-unavailable',
        query: { redirect: to.fullPath },
      };
    }
    const mode = match.meta.permissionMode ?? 'all';
    if (!permissions.can(requirement, mode)) {
      const missing = Array.isArray(requirement) ? requirement.join(', ') : requirement;
      return {
        path: '/erp/permission-denied',
        query: { permission: missing, redirect: to.fullPath },
      };
    }
  }
  return true;
}
